/***
*
*   PRIMARY ACTION
*   La accion principal de cada modulo (Registrar Vehiculo, Nueva Ruta...)
*   vive en el menu lateral y cambia segun la pantalla activa.
*
*   En movil el riel lateral se colapsa detras del hamburguesa, asi que ahi
*   el boton se pinta en linea, en la fila del titulo (ver PrimaryAction).
*
*   El titulo y el subtitulo NO pasan por aqui: los pinta PageHeader
*   arriba del contenido de cada vista.
*
**********/

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Style from './primaryAction.module.scss';

const PageContext = createContext({ action: null, setAction: () => {} });

export function PrimaryActionProvider({ children }) {
  const [action, setAction] = useState(null);
  const value = useMemo(() => ({ action, setAction }), [action]);

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}

export function usePrimaryActionSlot() {
  return useContext(PageContext).action;
}

export function usePrimaryAction(label, onClick) {
  const { setAction } = useContext(PageContext);

  // El callback vive en una ref para que cambiar de identidad en cada
  // render no vuelva a disparar el efecto.
  const callback = useRef(onClick);
  callback.current = onClick;

  useEffect(() => {
    if (!label) return undefined;

    setAction({ label, onClick: (...args) => callback.current?.(...args) });
    return () => setAction(null);
  }, [label, setAction]);
}

/***
*   Version declarativa: se pone donde va el boton dentro de la vista.
*   Registra la accion en el riel lateral (escritorio) y ademas pinta el
*   boton en su lugar cuando la pantalla es de movil, donde el riel no
*   esta a la vista. El CSS decide cual de los dos se ve; nunca los dos.
*
*     <PrimaryAction label="Registrar Vehículo" onClick={openCreateModal} />
**********/
export function PrimaryAction({ label, onClick }) {
  usePrimaryAction(label, onClick);

  if (!label) return null;

  // Se llama sin argumentos, igual que el riel: hay vistas que usan el primer
  // parametro para otra cosa y el evento del click se las rompe.
  return (
    <button type='button' className={Style.cta} onClick={() => onClick?.()} title={label}>
      <span className={Style.plus} aria-hidden='true'>+</span>
      <span className={Style.label}>{label}</span>
    </button>
  );
}

